import { redirectToPath } from '../router/routerUtils'
import { useUserStore } from '@/store/userStore'

// Function to check if a user is authenticated
// Also checks if store is still loading and waits if needed
const isAuthenticated = async (): Promise<boolean> => {
  const store = useUserStore.getState()
  
  // If store is still loading, wait for it to complete (with timeout)
  if (store.isLoading) {
    // Wait for loading to complete by checking periodically
    // This handles the case where initializeUserStore is still running
    const maxWaitTime = 5000 // 5 seconds max wait
    const startTime = Date.now()
    
    await new Promise<void>((resolve) => {
      const checkLoading = () => {
        const currentState = useUserStore.getState()
        const elapsed = Date.now() - startTime
        
        if (!currentState.isLoading || elapsed > maxWaitTime) {
          resolve()
        } else {
          // Check again after a short delay
          setTimeout(checkLoading, 50)
        }
      }
      checkLoading()
    })
  }
  
  // After loading completes, check authentication
  const finalState = useUserStore.getState()
  // Check both isAuthenticated flag and user existence for reliability
  return finalState.isAuthenticated || !!finalState.user
}

// Function to get a user's role from userStore
export const getUserRole = (): string => {
  const user = useUserStore.getState().user
  if (user?.role) {
    return user.role
  }
  // Fallback to localStorage role (for backward compatibility during migration)
  return localStorage.getItem('role') || 'GUEST'
}

// Function to check if the user has one of the allowed roles
const hasAllowedRole = (allowedRoles: string[]): boolean => {
  const currentRole = getUserRole().toUpperCase()
  return allowedRoles.some((role) => currentRole === role.toUpperCase())
}

// Route guard for authenticated users
export const guardAuthenticated = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }
  return {}
}

// Route guard for admin users
export const guardAdminOnly = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['ADMIN', 'SuperAdmin'])) {
// console.logUser not admin, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  return {}
}

// Route guard for teacher users
export const guardTeacherOnly = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['TEACHER'])) {
// console.logUser not teacher, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  return {}
}

// Route guard for student users
export const guardStudentOnly = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['STUDENT'])) {
// console.logUser not student, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  // console.log('Student route guard passed')
  return {}
}

// Route guard for parent users
export const guardParentOnly = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['PARENT'])) {
// console.logUser not parent, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  return {}
}

// Route guard for teacher, student, or parent users
export const guardTeacherStudentParent = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['TEACHER', 'STUDENT', 'PARENT'])) {
// console.logUser not teacher/student/parent, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  return {}
}

// Route guard for student or B2C_Client users
export const guardStudentOrB2CClient = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['STUDENT', 'B2C_CLIENT'])) {
// console.logUser not student or B2C_Client, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  console.log('Student or B2C_Client route guard passed')
  return {}
}

// Route guard for super admin users (school directors)
export const guardSuperAdminOnly = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['SuperAdmin'])) {
// console.logUser not super admin, redirecting to unauthorized')
    return redirectToPath('/unauthorized')
  }
  return {}
}

// Route guard for JeridSchool admin users (platform owners / app team)
export const guardJeridSchoolAdminOnly = async () => {
  const authenticated = await isAuthenticated()
  if (!authenticated) {
    return redirectToPath('/login')
  }

  if (!hasAllowedRole(['Jerid_School_Admin'])) {
    return redirectToPath('/unauthorized')
  }
  return {}
}

// Function to check if user has all required data (using userStore)
const hasRequiredUserData = (): boolean => {
  const user = useUserStore.getState().user
  const hasRole = !!user?.role
  const hasId = !!user?.id
  const hasEmail = !!user?.email
  const hasLastname = !!user?.lastname
  const hasFirstname = !!user?.firstname

  // console.log('Auth check:', {
  //   hasRole,
  //   hasId,
  //   hasEmail,
  //   hasLastname,
  //   hasFirstname,
  // })

  return hasRole && hasId && hasEmail && hasLastname && hasFirstname
}

// Route guard for quizz
export const guardQuizz = async () => {
  // Wait for authentication check first
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }
  
  if (!hasRequiredUserData()) {
// console.logMissing required user data, redirecting to login')
    return redirectToPath('/login')
  }
  return {}
}

// Route guard for notes
export const guardNotes = async () => {
  // Wait for authentication check first
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }
  
  if (!hasRequiredUserData()) {
// console.logMissing required user data, redirecting to login')
    return redirectToPath('/login')
  }
  return {}
}

// Route guard for focus-room
export const guardFocusRoom = async () => {
  // Wait for authentication check first
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }
  
  if (!hasRequiredUserData()) {
// console.logMissing required user data, redirecting to login')
    return redirectToPath('/login')
  }
  return {}
}

// Route guard for whiteboard
export const guardWhiteboard = async () => {
  // Wait for authentication check first
  const authenticated = await isAuthenticated()
  if (!authenticated) {
// console.logUser not authenticated, redirecting to login')
    return redirectToPath('/login')
  }
  
  if (!hasRequiredUserData()) {
// console.logMissing required user data, redirecting to login')
    return redirectToPath('/login')
  }
  return {}
}
